import type React from "react";
import { Box, Grid, Typography } from "@mui/material";
import type { Proposal } from "../types/proposal.types";
import EventPreviewCard from "./components/EventPreviewThumbnail";
import muzukuruLogo from "../../../assets/muzukuru.png";
import { formatDateToLongString } from "../../../utils/FormatDate";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import StreamsSection from "./components/LinksSections";
import IntroductionLetterRenderer from "./IntroductionLetterRenderer";
import DynamicThumbnailCard from "../../stream-management/components/thumbnail/ThumbnailCard";

interface MainProposalPageProps {
  proposal: Proposal;
  stream_id: number;
}
const cardSx = {
  border: "1px solid",
  borderColor: "grey.300",
  borderRadius: 2,
  p: 3,
  bgcolor: "#fff",
};

const MainProposalPage: React.FC<MainProposalPageProps> = ({
  proposal,
  stream_id,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const isEventProposal = Boolean(proposal.eventTitle);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  console.log("This is my proposal data ", proposal);

  return (
    <Box
      id="main-page"
      sx={{
        p: 6,
        height: "1056px",
        width: "816px",
        overflow: "hidden",
        transform: "scale(0.9)",
        transformOrigin: "top left",
      }}
    >
      {/* Proposal Info */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "stretch",
          mb: 4,
          gap: 4,
        }}
      >
        {/* Client Information Card */}
        <Box sx={{ ...cardSx, flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: "primary.main",
              textAlign: "center",
            }}
          >
            Client Information
          </Typography>

          <Box sx={{ lineHeight: 1.8 }}>
            <Typography variant="h4">
              Name:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {proposal.client.name}
              </Box>
            </Typography>

            {proposal.client.email && (
              <Typography variant="h4">
                Email:{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {proposal.client.email}
                </Box>
              </Typography>
            )}

            {proposal.client.phone && (
              <Typography variant="h4">
                Phone:{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {proposal.client.phone}
                </Box>
              </Typography>
            )}
          </Box>
        </Box>

        {/* Event / Service Information Card */}
        <Box sx={{ ...cardSx, flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: "primary.main",
              textAlign: "center",
            }}
          >
            {isEventProposal ? "Event Information" : "Service Information"}
          </Typography>

          <Box sx={{ lineHeight: 1.8 }}>
            {isEventProposal ? (
              <>
                <Typography variant="h4">
                  Event Title:{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {proposal.eventTitle}
                  </Box>
                </Typography>

                <Typography variant="h4">
                  Date:{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {formatDate(
                      proposal.booking?.events?.[0]?.start_date_of_event ||
                      proposal.booking?.services?.[0]?.date_of_service
                    )}
                  </Box>
                </Typography>
                <Typography variant="h4">
                  Venue:{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {`${proposal.booking?.services?.[0]?.venue.name || ""} ${
                      proposal.booking?.services?.[0]?.venue.address_one || ""
                    }`}
                  </Box>
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h4">
                  In Memory of:{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {proposal.deceasedName}
                  </Box>
                </Typography>

                <Typography variant="h4">
                  Date:{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {formatDate(
                      proposal.booking?.services?.[0].date_of_service,
                    )}
                  </Box>
                </Typography>

                <Typography variant="h4">
                  Venue:{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {`${proposal.booking?.services?.[0]?.venue.name || ""} ${
                      proposal.booking?.services?.[0]?.venue.address_one || ""
                    }`}
                  </Box>
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Introduction Letter */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          {isEventProposal
            ? "Our Commitment to Your Event"
            : "Our Commitment to You"}
        </Typography>
        <IntroductionLetterRenderer proposal={proposal} />
      </Box>

      {/* Thumbnail Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
        >
          {proposal?.eventTitle
            ? "Event Preview Thumbnail"
            : "Sample Thumbnail (Preview Only)"}
        </Typography>

        {/* Conditional rendering */}
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {/* Thumbnail Section */}
          <Grid size={{ xs: 12, md: 8 }}>
            {proposal?.eventTitle ? (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <EventPreviewCard
                  eventTitle={proposal.eventTitle}
                  eventDate={formatDateToLongString(
                    proposal.booking?.events?.[0]?.start_date_of_event || "",
                  )}
                  streamTime={proposal.booking?.events?.[0]?.start_time || ""}
                  streamLocation={
                    proposal.booking?.events?.[0]?.venue?.name || ""
                  }
                  locationDetails={
                    proposal.booking?.events?.[0]?.venue?.city || ""
                  }
                  sponsorName="MUZUKURU"
                  sponsorLogo={muzukuruLogo}
                  isLiveStream={true}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: 1200,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {/* <FuneralPreviewCard
                  deceasedName={`${
                    proposal.booking?.deceased?.[0]?.first_name || ""
                  } ${proposal.booking?.deceased?.[0]?.surname || ""}`}
                  birthDate={formatDateToLongString(
                    proposal.booking?.deceased?.[0]?.date_of_birth || "",
                  )}
                  deathDate={formatDateToLongString(
                    proposal.booking?.deceased?.[0]?.date_of_death || "",
                  )}
                  serviceDate={formatDateToLongString(
                    proposal.booking?.services?.[0]?.date_of_service || "",
                  )}
                  serviceTime={
                    proposal.booking?.services?.[0]?.start_time || ""
                  }
                  timeZone="CAT"
                  location={`${
                    proposal.booking?.services?.[0]?.venue.name || ""
                  } ${
                    proposal.booking?.services?.[0]?.venue.address_one || ""
                  }`}
                  isLiveStream={true}
                  circleImageUrl={
                    proposal.booking?.deceased?.[0]?.person_image || ""
                  }
                  backgroundImage={thumbnail_background_image || ""}
                  broadcastBy="MUZUKURU"
                /> */}

                <DynamicThumbnailCard streamId={stream_id} height={1200} />
              </Box>
            )}
          </Grid>

          {proposal?.additional_notes &&
            proposal.additional_notes.trim() !== "" && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Box>
                  <textarea
                    value={proposal.additional_notes || ""}
                    readOnly
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      resize: "vertical",
                      padding: "10px",
                      borderRadius: "8px",
                      border: `1px solid ${reduxTheme.primary_colors[200]}`,
                      color: "#333",
                      fontSize: "0.9rem",
                      lineHeight: "1.5",
                      fontFamily: "inherit",
                    }}
                  />
                </Box>
              </Grid>
            )}
        </Grid>
      </Box>

      {/* Previous Streams or Events */}
      {proposal.show_previous_booking_links &&
        proposal.previous_booking_links &&
        proposal.previous_booking_links.length > 0 && (
          <StreamsSection
            reduxTheme={reduxTheme}
            previousBookingLinks={proposal.previous_booking_links}
          />
        )}
    </Box>
  );
};

export default MainProposalPage;
